// ISO calendar arithmetic for the PayFlow screens.
//
// Lifted out of ManagePayFlow.tsx when the pull calendar was built and needed the same
// three functions. Relocated rather than duplicated: two copies of date arithmetic drift,
// and the way they drift is that one of them starts crossing day boundaries.
//
// Everything here stays in LOCAL calendar terms. A payroll date is a business day, not
// an instant — `2026-08-12` is the day the business calls the 12th, whatever timezone
// the browser is in. Round-tripping through UTC is how a date becomes the 11th for
// someone in Vancouver, so no function here converts to or from it.

import { ymdFromISO } from "./payflowStore";

// A Date rendered as the local calendar day it represents.
export const isoOf = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(
    2,
    "0",
  )}`;

// Shifting an ISO date by whole days, staying in local calendar terms so a date
// never slips across a day boundary the way a UTC round trip can.
export const isoAddDays = (iso: string, days: number): string => {
  const { year, monthIndex, day } = ymdFromISO(iso);
  const d = new Date(year, monthIndex, day);
  d.setDate(d.getDate() + days);
  return isoOf(d);
};

// Whole days from one ISO date to another. Rounded because the two local midnights
// are an hour apart across a DST boundary.
export const daysBetweenISO = (startISO: string, endISO: string): number => {
  const a = ymdFromISO(startISO);
  const b = ymdFromISO(endISO);
  const ms =
    new Date(b.year, b.monthIndex, b.day).getTime() -
    new Date(a.year, a.monthIndex, a.day).getTime();
  return Math.round(ms / 86_400_000);
};

// The number of days a range covers, both ends included — the span an admin sees when
// they pick 1st to 15th and are told "15 days". Off-by-one bugs in range limits are
// nearly always this, so it is named rather than written out at each site.
export const inclusiveSpanDays = (startISO: string, endISO: string): number =>
  daysBetweenISO(startISO, endISO) + 1;

// "YYYY-MM" for the month an ISO date falls in. The key coverage is cached under, so
// it has to be derived the same way everywhere.
export const monthKeyOf = (iso: string): string => iso.slice(0, 7);

// Day of the week, 0 = Sunday. The calendar grid is laid out Sunday-first to match the
// pay week, so this is the column an ISO date sits in.
export const weekdayOfISO = (iso: string): number => {
  const { year, monthIndex, day } = ymdFromISO(iso);
  return new Date(year, monthIndex, day).getDay();
};

// How many days the month containing `iso` has.
export const daysInMonth = (iso: string): number => {
  const { year, monthIndex } = ymdFromISO(iso);
  // Day 0 of the next month is the last day of this one.
  return new Date(year, monthIndex + 1, 0).getDate();
};

export const firstOfMonthISO = (iso: string): string => `${iso.slice(0, 7)}-01`;

// The same day-of-month `months` away, clamped to the target month's length so
// stepping off the 31st never overflows into the following month.
export const addMonthsISO = (iso: string, months: number): string => {
  const { year, monthIndex, day } = ymdFromISO(iso);
  const target = new Date(year, monthIndex + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return isoOf(new Date(target.getFullYear(), target.getMonth(), Math.min(day, lastDay)));
};
